/**
 * 組織レポート v3 のデータ取得。
 *
 * PC向け（/analysis/org-report-v3）とモバイル向け（/embed/report/org）の
 * 両方から使う。算出・文言はすべて utils 側に閉じているので、ここは取得と
 * 組み立ての呼び出しだけを行う。
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { analysisApi } from '../api/analysisApi';
import { unwrapGb74Scores } from '../utils/gb74Report';
import {
  buildOrgReportV3,
  type OrgMemberV3,
  type OrgReportV3Data,
} from '../utils/buildOrgReportV3';
import { demoConsultDepts, demoConsultDepts6 } from '../utils/orgConsultDemoV3';

/** 「2026年8月」形式（集計対象の期間表示）。 */
export const formatPeriod = (d: Date): string => `${d.getFullYear()}年${d.getMonth() + 1}月`;

/** 「2026.08.22」形式（レポート日付）。 */
export const formatReportDate = (d: Date): string =>
  `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

export interface OrgReportV3State {
  data: OrgReportV3Data | null;
  loading: boolean;
  error: string | null;
  /** つながりの構造にデモデータを使っているか */
  usingDemo: boolean;
  reload: () => void;
}

export interface OrgReportV3Options {
  /**
   * つながりの構造（相談ネットワーク）を取りに行かない。
   * ホームの「組織の評価」カードのように、3スコアと組織タイプしか出さない画面で使う。
   * 余分なAPI往復を減らすためだけの指定で、算出結果は変わらない。
   */
  skipConsult?: boolean;
}

export function useOrgReportV3Data(options: OrgReportV3Options = {}): OrgReportV3State {
  const skipConsult = options.skipConsult ?? false;
  const { user } = useAuth();
  const companyId = user?.companyId;
  // 埋め込みでは会社名が取れないことがある。取れないときは会社名を出さずに済むよう
  // 汎用の見出しにする（旧実装も「会社全体」と表示していた）。
  const companyName = user?.companyName || '会社全体';

  // ?consult=demo  … 赤・黄・緑が出るデモ
  // ?consult=demo6 … 6名の部署が4パターン出るデモ（判定下限を6名にする）
  const consultMode =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('consult')
      : null;
  const useDemo6 = consultMode === 'demo6';
  const usingDemo = consultMode === 'demo' || useDemo6;

  const [data, setData] = useState<OrgReportV3Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // companyId は指定しない場合サーバがトークンから会社を判定する。
      // WebView 埋め込み（/embed/report/org）ではネイティブがトークンだけを注入するため
      // AuthContext の user に companyId が入らない。旧実装も引数なしで呼んでいた。
      const response = await analysisApi.getCompanyResults(companyId || undefined);
      const members: OrgMemberV3[] = [];
      for (const r of response.results ?? []) {
        const gb74 = unwrapGb74Scores((r as { scores?: unknown }).scores);
        if (!gb74) continue;
        members.push({
          name: (r as { userName?: string }).userName,
          scores: gb74,
          departmentName: (r as { departmentName?: string | null }).departmentName ?? null,
        });
      }
      if (members.length === 0) {
        setError('分析結果がまだありません。社員の回答が完了すると表示されます。');
        return;
      }

      // つながりの構造。取得できなくても他のセクションは出す（best-effort）。
      let consultDepts;
      if (skipConsult) {
        consultDepts = undefined;
      } else if (useDemo6) {
        consultDepts = demoConsultDepts6();
      } else if (usingDemo) {
        consultDepts = demoConsultDepts();
      } else {
        try {
          const net = await analysisApi.getCompanyConsultNetwork();
          consultDepts = net.consultNetwork?.depts?.map((d) => ({
            dept: d.dept,
            members: d.members.map((m) => ({ id: m.id, name: m.name })),
            edges: d.edges.map((e) => ({ from: e.from, to: e.to })),
          }));
        } catch {
          consultDepts = undefined;
        }
      }

      setData(
        buildOrgReportV3(members, {
          companyName,
          period: formatPeriod(new Date()),
          consultDepts,
          consultMinJudge: useDemo6 ? 6 : undefined,
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'レポートの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [companyId, companyName, usingDemo, useDemo6, skipConsult]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, usingDemo, reload: () => void load() };
}
